<template>
  <div class="inspection-detail-modal">
    <HKLModal
      ref="modal"
      :title="modalTitle"
      :closable="true"
      size="large"
      :dialogStyle="{'padding-top':'40px', 'margin-top': '0%', 'top':'0px'}"
    >
      <div class="inspection-detail-modal__content">
        <div
          class="edit-form row"
        >
          <div class="column small-6">
            <label :class="{'required-flag': canEdit}">{{ $t('tenantProjects.inspection.type') }}</label>
            <HKLSelect
              v-if="canEdit"
              :value="inspection.type || undefined"
              :options="typeHklOptions"
              :required="true"
              :showSearch="true"
              placeholder=""
              @change="val => { inspection.type = val }"
            />
            <span v-else>{{ customInsType(inspection.type) }}</span>
          </div>
          <div class="column small-6">
            <label :class="{'required-flag': canEdit}">{{ $t('tenantProjects.inspection.itemInspection') }}</label>
            <HKLSelect
              v-if="canEdit"
              :value="inspection.itemInspection || undefined"
              :options="itemInspectionHklOptions"
              :required="true"
              :showSearch="true"
              placeholder=""
              @change="val => { inspection.itemInspection = val }"
            />
            <span v-else>{{ itemInspectionOptionLabels(inspection.itemInspection) }}</span>
          </div>
          <div
            v-if="isSelectedOther"
            class="column small-12"
          >
            <label :class="[{'required-flag': canEdit}, 'additional-hint']">{{ $t('tenantProjects.inspection.additionalInfoLabel') }}</label>
            <TextField
              v-if="canEdit"
              :showColon="false"
              :value="inspection.additionalInfo"
              :dirtyCheck="false"
              :required="true"
              @updateValue="val => inspection.additionalInfo = val"
            />
            <div v-else>
              {{ inspection.additionalInfo | isEmptyValue }}
            </div>
          </div>
          <div
            class="column small-12"
          >
            <label :class="{'required-flag': canEdit}">{{ $t('tenantProjects.inspection.descOfWork') }}</label>
            <TextField
              v-if="canEdit"
              :showColon="false"
              :value="inspection.descOfWork"
              :dirtyCheck="false"
              :required="true"
              @updateValue="val => inspection.descOfWork = val"
            />
            <div v-else>
              {{ inspection.descOfWork | isEmptyValue }}
            </div>
          </div>
          <div class="column small-6">
            <label :class="{'required-flag': canEdit}">{{ $t('tenantProjects.inspection.dateTime') }}</label>
            <DateTimeSelector
              v-if="canEdit"
              :date="inspection.date"
              :timeOptions="timeOptions"
              :showClearBtn="true"
              :required="true"
              @updateDate="(val) => inspection.date = val"
            />
            <span v-else>{{ inspection.date | formatDate(('DD MMM YY HH:mm')) }}</span>
          </div>
          <div
            class="column small-12"
          >
            <label>{{ $t('tenantProjects.inspection.remarks') }}</label>
            <TextField
              v-if="canEdit"
              :showColon="false"
              :value="inspection.remarks"
              :dirtyCheck="false"
              @updateValue="val => inspection.remarks = val"
            />
            <pre v-else>{{ inspection.remarks | isEmptyValue }}</pre>
          </div>
          <div
            class="column small-4"
          >
            <label :class="{'required-flag': canEdit}">{{ $t('tenantProjects.inspection.handledBy') }}</label>
            <InputField
              v-if="canEdit"
              :showColon="false"
              :showFlag="false"
              :value="inspection.handledBy"
              :required="true"
              :dirtyCheck="false"
              @updateValue="val => inspection.handledBy = val"
            />
            <span v-else>{{ inspection.handledBy | isEmptyValue }}</span>
          </div>
          <div
            class="column small-12 upload-area"
          >
            <InspectionAttachments :mailHistories="mailHistories" />
          </div>
        </div>
        <HKLTable
          class="inspection__history-table"
          :dataSource="mailHistories"
          :columns="mailHistoryCols"
        />
        <div
          v-if="lastUpdatedText"
          class="last-update"
        >
          <div class="last-update__text">
            {{ lastUpdatedText }}
          </div>
        </div>
      </div>
      <div slot="footer">
        <template
          v-if="canEdit"
        >
          <HKLButton
            color="darkblue"
            action="cancel"
            @click="() => close()"
          />
          <HKLButton
            color="darkblue"
            action="save"
            @click="saveAction"
          />
          <HKLButton
            color="orange"
            action="send"
            @click="send"
          />
        </template>
      </div>
    </HKLModal>
    <InspectionSendModal
      ref="sendModal"
      :afterClose="closeSendPopUp"
    />
  </div>
</template>

<script>
import { getKey } from '@/api/common/request'
import * as inspectionAPI from '@/api/inspection/request'
import HKLButton from '@/controls/Button/HKLButton'
import DateTimeSelector from '@/controls/DateTimeSelector'
import InputField from '@/controls/InputField.vue'
import HKLModal from '@/controls/Modal/HKLModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import EmailsCell from '@/controls/Table/EmailsCell.vue'
import HKLTable from '@/controls/Table/HKLTable.vue'
import TextField from '@/controls/TextField.vue'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { useRouteParamId } from '@/router/useRouter'
import { useStoreActions, useStoreGetters } from '@/store/index'
import { emptyValue } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import lodash from 'lodash'
import { computed, getCurrentInstance, reactive, ref, toRefs, unref } from 'vue-demi'
import InspectionAttachments from './InspectionAttachments.vue'
import InspectionSendModal from './InspectionSendModal.vue'
import { useCurrentInspection } from './Provider.vue'
import { ITEM_INSPECTION, SEND_STATUS, TYPES } from './consts'
import { useCanUpdateInspection, useItemInspectedOptions } from './hooks'
import inspectionMixin from './inspectionMixin'

export default {
  name: 'EditInspectionModal',
  components: {
    HKLTable,
    HKLButton,
    DateTimeSelector,
    HKLSelect,
    TextField,
    HKLModal,
    InputField,
    InspectionAttachments,
    InspectionSendModal,
  },
  mixins: [inspectionMixin],
  props: {
    afterSaved: {
      type: Function,
      default: () => {},
    },
  },
  setup (props, { emit }) {
    const dateTimeFormat = 'DD MMM YY HH:mm'
    const userStore = useStoreGetters('user', ['currentUser', 'isHKlandUser', 'isTenant'])
    const { loadAllInspections } = useStoreActions('tenantProject', ['loadAllInspections'])
    const { proxy } = getCurrentInstance()
    const inspection = useCurrentInspection()
    const inspectionHistories = ref([])
    const modal = useRefModal()
    const sendModal = useRefModal()
    const isCreating = ref(false)
    const itemInspectionOptions = useItemInspectedOptions()
    const setupData = reactive({
      ...modal,
      sendModal: sendModal.modal,
      isCreating,
      inspection,
      ...userStore,
      projectId: useRouteParamId(),
      canEdit: useCanUpdateInspection(),
      typeOptions: ref(Object.values(TYPES)),
      timeOptions: dateUtil.generateTimeDurations(15),
      itemInspectionOptions: computed(() => Object.keys(unref(itemInspectionOptions))),
      itemInspectionOptionLabels: (option) => unref(itemInspectionOptions)[option] || emptyValue,
      typeHklOptions: computed(() => Object.values(TYPES).map((type) => ({
        name: proxy.customInsType(type),
        value: type,
      }))),
      itemInspectionHklOptions: computed(() => Object.keys(unref(itemInspectionOptions)).map((key) => ({
        name: unref(itemInspectionOptions)[key] || emptyValue,
        value: key,
      }))),
      emptyValue: proxy.$t('emptyValue'),
      modalTitle: computed(() => {
        const titleKey = setupData.canEdit ? 'editDetailTitle' : 'detail'
        return proxy.$t(`tenantProjects.inspection.${titleKey}`)
      }),
      isSelectedOther: computed(() => {
        return setupData.inspection.itemInspection === ITEM_INSPECTION.Others
      }),
      lastUpdatedText: computed(() => {
        if (!inspection.value.updater) return false
        const updater = inspection.value.updater
        const userName = userHelper.getUserName(updater.username, updater.displayName)
        return proxy.$t('lastUpdatedBy', [userName, dateUtil.format(inspection.value.updatedAt, dateTimeFormat)])
      }),
      mailHistoryCols: computed(() => {
        const result = [
          {
            title: proxy.$t('sender'),
            dataIndex: 'sender',
          },
          {
            title: proxy.$t('to'),
            dataIndex: 'to',
            component: {
              name: EmailsCell,
              attrs: (value) => {
                return { emails: value }
              },
            },
          },
          {
            title: proxy.$t('cc'),
            dataIndex: 'cc',
            component: {
              name: EmailsCell,
              attrs: (value) => {
                return { emails: value }
              },
            },
          },
          {
            title: proxy.$t('bcc'),
            dataIndex: 'bcc',
            component: {
              name: EmailsCell,
              attrs: (value) => {
                return { emails: value }
              },
            },
          },
          {
            title: proxy.$t('time'),
            dataIndex: 'time',
          },
          {
            title: '',
            dataIndex: 'viewLink',
            component: {
              name: 'a',
              attrs: () => {
                return { class: 'underline' }
              },
              listeners: (_, record) => ({
                click: () => sendModal.open(record.raw),
              }),
            },
          },
        ]
        userStore.isTenant.value && result.splice(3, 1)
        return result
      }),
      mailHistories: computed(() => {
        return inspectionHistories.value
          .filter(item => item.status === SEND_STATUS.SENT)
          .map(item => {
            return {
              ...item,
              sender: item.updater.displayName,
              time: dateUtil.format(item.updatedAt, dateTimeFormat),
              viewLink: proxy.$t('view'),
            }
          })
      }),

      createNewRecord: async ({type = ''} = {}) => {
        inspectionHistories.value = []
        isCreating.value = true
        inspection.value = {
          id: await getKey(),
          type: type,
          itemInspection: '',
          date: null,
          remarks: '',
          descOfWork: '',
          additionalInfo: '',
          isCreating,
        }
        modal.open()
      },
      openCurrentRecord: async (record) => {
        isCreating.value = false
        fusionInputCheckUtil.resetAllFields()
        inspection.value = await inspectionAPI.getById(record.id)
        await setupData.refreshHistories()
        modal.open()
      },
      validate: () => {
        fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.edit-form')
        return fusionInputCheckUtil.isAllFieldsValid('.edit-form')
      },
      save: async () => {
        if (!setupData.validate()) return false
        if (isCreating.value) {
          await inspectionAPI.create({ ...inspection.value, projectId: setupData.projectId })
          isCreating.value = false
        } else {
          await inspectionAPI.update(inspection.value.id, inspection.value)
        }
        fusionInputCheckUtil.resetAllFields()
        notification.fireNotification(true, proxy.$t('success'))
        await loadAllInspections(setupData.projectId)
        await setupData.refreshDetail()
        return true
      },
      saveAction: async () => {
        const isSaved = await setupData.save()
        isSaved && await props.afterSaved(modal)
      },
      refreshDetail: async () => {
        inspection.value = await inspectionAPI.getById(inspection.value.id)
      },
      send: async () => {
        const isSaved = await setupData.save()
        if (isSaved) {
          let historyData = {}

          const histories = unref(inspectionHistories)
          if (histories.length) {
            const latestHistory = histories[0]
            // If is draft, edit current content, if is not draft, create new one basic on last history content.
            historyData = latestHistory.status === SEND_STATUS.DRAFT ? latestHistory : {
              ...lodash.pick(latestHistory, ['to', 'cc', 'bcc', 'content']),
              status: SEND_STATUS.DRAFT,
              attachmentList: [],
            }
          }

          sendModal.open(histories.length ? historyData : undefined)
        }
      },
      closeSendPopUp: async () => {
        formUtils.resetDirty()
        await setupData.refreshHistories()
        await props.afterSaved(modal)
      },
      refreshHistories: async () => {
        const histories = await inspectionAPI.getEmailHistoriesById(inspection.value.id, { isSilent: true })
        inspectionHistories.value = lodash.orderBy(histories, ['createdAt'], ['desc']) || []
      },
    })
    return {
      ...toRefs(setupData),
    }
  },
}
</script>

<style lang="scss" scoped>
.additional-hint {
  font-size: 12px;
  color: #878787;
  font-weight: normal;
  font-style: italic;
}
.inspection__history-table ::v-deep {
  margin: 0;
  .ant-table-tbody > tr > td {
    overflow-wrap: anywhere;
  }
  td.sender {
    width: 14%;
  }
  td.time {
    width: 13%;
  }
  td.view-link {
    width: 66px;
  }
}
.edit-form {
  flex-flow: column;
  ::v-deep {
    textarea {
      width: 100%;
    }
    label {
      margin-top: 0;
    }
  }
  .column {
    margin-bottom: 15px;
    &.upload-area {
      margin-bottom: 0;
    }
  }
}
.last-update {
  margin-top: 5px;
}
</style>
